import { RefObject, useEffect } from "react";

/** Measure geometry only: CSS bounds photo growth and content always stays in flow. */
export function useJourneyComposition(root: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const element = root.current;
    const header = document.querySelector("header");
    const section = element?.closest("section");
    const panels = element?.querySelector<HTMLElement>(".journey-panels");
    const intro = element?.querySelector(".journey-introduction");
    const navigation = section?.querySelector<HTMLElement>(".journey-navigation");
    if (!element || !panels || !navigation || !header) return;
    const media = window.matchMedia("(max-width: 1023px)");
    let frame = 0;
    const number = (value: string) => Number.parseFloat(value) || 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!media.matches) return;
        const card = panels.querySelector<HTMLElement>(".journey-panel");
        const photo = panels.querySelector<HTMLElement>(".journey-photo");
        if (!card) return;
        const style = getComputedStyle(card);
        const visual = window.visualViewport;
        // Pinch zoom must not squeeze the layout; text enlargement grows normally.
        const viewport = visual && visual.scale === 1
          ? Math.min(visual.height, window.innerHeight)
          : document.documentElement.clientHeight;
        const introHeight = panels.getBoundingClientRect().top - element.getBoundingClientRect().top;
        const navigationStyle = getComputedStyle(navigation);
        const navigationSpace = navigation.getBoundingClientRect().height + number(navigationStyle.marginTop);
        const safeArea = section ? number(getComputedStyle(section).paddingBottom) : 0;
        const cardGap = number(getComputedStyle(panels).rowGap);
        const ctaHeight = Math.max(44, ...Array.from(panels.querySelectorAll(".journey-cta"), cta => cta.getBoundingClientRect().height));
        const frameSpace = number(style.paddingTop) + number(style.paddingBottom) + number(style.borderTopWidth) + number(style.borderBottomWidth);
        const rowSpace = (viewport - header.getBoundingClientRect().height - introHeight - navigationSpace - safeArea - cardGap) / 2
          - frameSpace - ctaHeight - number(style.rowGap);
        element.style.setProperty("--journey-row-space", `${Math.max(0, rowSpace)}px`);
        element.style.setProperty("--journey-photo-width", `${photo?.getBoundingClientRect().width ?? 0}px`);
      });
    };
    const observer = new ResizeObserver(measure);
    for (const target of [element, header, intro, navigation, ...panels.querySelectorAll(".journey-cta")]) {
      if (target) observer.observe(target);
    }
    measure();
    media.addEventListener("change", measure);
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      media.removeEventListener("change", measure);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [root]);
}